import { useCallback, useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { usePlayerActions } from '@cartwhl/3d/player';

import { ignoreUndoRedoHotkey as shouldIgnoreMotionEditorHotkey } from '../../utils';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import { useKeyPoseFrameCaptureActions } from './useKeyPoseCaptureActions';
import { useKeyPosePersistenceActions } from './useKeyPosePersistenceActions';

interface UseKeyPoseHotkeysOptions {
  enabled?: boolean;
  onFocusSelectedJoint?: () => void;
}

interface KeyPoseFrameLike {
  frame: number;
}

export function useKeyPoseHotkeys({
  enabled = true,
  onFocusSelectedJoint,
}: UseKeyPoseHotkeysOptions = {}) {
  const keyPoseStore = useKeyPoseStore();
  const playerActions = usePlayerActions();
  const { saveCurrentKeyPoses } = useKeyPosePersistenceActions();
  const {
    addKeyPoseAtCurrentFrame,
    getCurrentFrame,
    getTimelineKeyPoses,
    removeKeyPose,
    toggleKeyPoseHidden,
  } = useKeyPoseFrameCaptureActions();
  const { actionStack, editing } = keyPoseStore.state;
  const hotkeysEnabled = enabled && !editing.dragging;
  const canUndo = hotkeysEnabled && actionStack.index >= 0;
  const canRedo =
    hotkeysEnabled && actionStack.index < actionStack.entries.length - 1;

  const undo = useCallback(() => {
    const result = keyPoseStore.undoKeyPoseAction();
    if (!result) {
      return;
    }

    void saveCurrentKeyPoses('undo-key-pose', {
      referenceName: result.referenceName,
    });
  }, [keyPoseStore, saveCurrentKeyPoses]);

  const redo = useCallback(() => {
    const result = keyPoseStore.redoKeyPoseAction();
    if (!result) {
      return;
    }

    void saveCurrentKeyPoses('redo-key-pose', {
      referenceName: result.referenceName,
    });
  }, [keyPoseStore, saveCurrentKeyPoses]);

  const jumpToPreviousKeyPose = useCallback(() => {
    const currentFrame = getCurrentFrame();
    const timelineKeyPoses = getTimelineKeyPoses();
    const frame = getPreviousKeyPoseFrame(currentFrame, timelineKeyPoses);
    if (frame === null) {
      return;
    }

    playerActions.setFrame(frame);
  }, [getCurrentFrame, getTimelineKeyPoses, playerActions]);

  const jumpToNextKeyPose = useCallback(() => {
    const currentFrame = getCurrentFrame();
    const timelineKeyPoses = getTimelineKeyPoses();
    const frame = getNextKeyPoseFrame(currentFrame, timelineKeyPoses);
    if (frame === null) {
      return;
    }

    playerActions.setFrame(frame);
  }, [getCurrentFrame, getTimelineKeyPoses, playerActions]);

  const addKeyPoseIfMissing = useCallback(() => {
    addKeyPoseAtCurrentFrame();
  }, [addKeyPoseAtCurrentFrame]);

  const removeCurrentKeyPose = useCallback(() => {
    const currentFrame = getCurrentFrame();
    const currentFrameKeyPose =
      getTimelineKeyPoses().find((keyPose) => keyPose.frame === currentFrame) ??
      null;
    if (!currentFrameKeyPose) {
      return;
    }

    removeKeyPose(currentFrameKeyPose.id);
  }, [getCurrentFrame, getTimelineKeyPoses, removeKeyPose]);

  const toggleCurrentKeyPoseHidden = useCallback(() => {
    const currentFrame = getCurrentFrame();
    const currentFrameKeyPose =
      getTimelineKeyPoses().find((keyPose) => keyPose.frame === currentFrame) ??
      null;
    if (!currentFrameKeyPose) {
      return;
    }

    toggleKeyPoseHidden(currentFrameKeyPose.id);
  }, [getCurrentFrame, getTimelineKeyPoses, toggleKeyPoseHidden]);

  const switchToIkMode = useCallback(() => {
    keyPoseStore.setKeyPoseEditingMode('ik');
  }, [keyPoseStore]);

  const switchToFkMode = useCallback(() => {
    keyPoseStore.setKeyPoseEditingMode('fk');
  }, [keyPoseStore]);

  const focusSelectedJoint = useCallback(() => {
    onFocusSelectedJoint?.();
  }, [onFocusSelectedJoint]);

  useHotkeys(
    'meta+z, ctrl+z',
    undo,
    {
      enabled: canUndo,
      ignoreEventWhen: shouldIgnoreMotionEditorHotkey,
      preventDefault: true,
    },
    [undo],
  );

  useHotkeys(
    'meta+shift+z, ctrl+shift+z, ctrl+y',
    redo,
    {
      enabled: canRedo,
      ignoreEventWhen: shouldIgnoreMotionEditorHotkey,
      preventDefault: true,
    },
    [redo],
  );

  useHotkeys(
    's',
    addKeyPoseIfMissing,
    {
      enabled: hotkeysEnabled,
      ignoreEventWhen: shouldIgnoreMotionEditorHotkey,
      preventDefault: true,
    },
    [addKeyPoseIfMissing],
  );

  useHotkeys(
    'shift+s',
    removeCurrentKeyPose,
    {
      enabled: hotkeysEnabled,
      ignoreEventWhen: shouldIgnoreMotionEditorHotkey,
      preventDefault: true,
    },
    [removeCurrentKeyPose],
  );

  useHotkeys(
    'h',
    toggleCurrentKeyPoseHidden,
    {
      enabled: hotkeysEnabled,
      ignoreEventWhen: shouldIgnoreMotionEditorHotkey,
      preventDefault: true,
    },
    [toggleCurrentKeyPoseHidden],
  );

  useHotkeys(
    'w',
    switchToIkMode,
    {
      enabled: hotkeysEnabled && editing.mode === 'fk',
      ignoreEventWhen: shouldIgnoreMotionEditorHotkey,
      preventDefault: true,
    },
    [switchToIkMode],
  );

  useHotkeys(
    'e',
    switchToFkMode,
    {
      enabled: hotkeysEnabled && editing.mode === 'ik',
      ignoreEventWhen: shouldIgnoreMotionEditorHotkey,
      preventDefault: true,
    },
    [switchToFkMode],
  );

  useHotkeys(
    'f',
    focusSelectedJoint,
    {
      enabled: hotkeysEnabled,
      ignoreEventWhen: shouldIgnoreMotionEditorHotkey,
      preventDefault: true,
    },
    [focusSelectedJoint],
  );

  useEffect(() => {
    if (!hotkeysEnabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreMotionEditorHotkey(event)) {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (event.key === '<' || event.key === ',') {
        event.preventDefault();
        jumpToPreviousKeyPose();
        return;
      }

      if (event.key === '>' || event.key === '.') {
        event.preventDefault();
        jumpToNextKeyPose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hotkeysEnabled, jumpToNextKeyPose, jumpToPreviousKeyPose]);
}

export function getNextKeyPoseFrame(
  currentFrame: number,
  keyPoses: readonly KeyPoseFrameLike[],
) {
  const frames = getSortedUniqueFrames(keyPoses);
  if (frames.length === 0) {
    return null;
  }

  return frames.find((frame) => frame > currentFrame) ?? frames[0] ?? null;
}

export function getPreviousKeyPoseFrame(
  currentFrame: number,
  keyPoses: readonly KeyPoseFrameLike[],
) {
  const frames = getSortedUniqueFrames(keyPoses);
  if (frames.length === 0) {
    return null;
  }

  for (let index = frames.length - 1; index >= 0; index -= 1) {
    const frame = frames[index];
    if (frame < currentFrame) {
      return frame;
    }
  }

  return frames.at(-1) ?? null;
}

export function getSortedUniqueFrames(keyPoses: readonly KeyPoseFrameLike[]) {
  return Array.from(
    new Set(keyPoses.map((keyPose) => keyPose.frame).filter(Number.isFinite)),
  ).sort((left, right) => left - right);
}
